import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { firstValueFrom } from 'rxjs';
import { NOTIFIER } from '@shared/notifier/types';
import { PdfPrintError, PdfPrintService } from '@shared/print/pdf-print.service';
import { safeFileName } from '@shared/utils/markdown-export.utils';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdBodygraphRenderStateFactory } from '../bodygraph/hd-bodygraph-render-state.factory';
import { HdChartSettingsStore } from '../bodygraph/chart-tabs/hd-chart-settings.store';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdChironSide, resolveHdChironPosition, toHdSimpleChironVm } from '../bodygraph/hd-chiron.models';
import {
  deriveBasicProperties,
  deriveLifeCross,
  formatHdIncarnationCrossValue,
  formatHdLifeCrossValue,
} from '../bodygraph/basic-properties/hd-chart-properties.util';
import { buildHdPrimaryConditioningVm } from '../bodygraph/basic-properties/hd-primary-conditioning-value.component';
import type { HdBodygraphChartPreset } from '../bodygraph/hd-bodygraph.models';
import { buildMandalaWheelLayout } from '../mandala/mandala.constants';
import {
  buildHdMandalaGateGlyphs,
  buildHdMandalaQuarterTexts,
  buildHdMandalaZodiacNames,
  hdMandalaSideGlyphColor,
} from '../mandala/mandala-wheel-inputs';
import { HdChartPrintSheetComponent } from './hd-chart-print-sheet.component';
import { HdChartPrintCrosses, HdChartPrintMandala, buildHdChartPrintBasics } from './hd-chart-print';
import { HdChartPrintOptions, resolveHdChartPrintColors } from './hd-chart-print-options';

const PROPERTIES_PREFIX = 'humanDesign.properties';
const CONTROLS_PREFIX = 'humanDesign.chart.controls';
/** Kruisnamen (incarnatiekruis in de kop van Regulier en Mandala). */
const CROSSES_SCOPE = 'human-design/crosses';

/**
 * Chart als PDF (tab-contextmenu → printdialoog): vult het printblad met de berekende chart van de
 * preset (niet met tijdelijke bewerkingen of overlays) volgens de keuzes uit de dialoog, en laat
 * PdfPrintService de rest doen. Teksten in de HD-contenttaal, zoals het Basis-paneel.
 */
@Injectable({ providedIn: 'root' })
export class HdChartPdfExportService {
  private readonly pdfPrint = inject(PdfPrintService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);
  private readonly settings = inject(HdChartSettingsStore);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly notifier = inject(NOTIFIER);

  async downloadChart(preset: HdBodygraphChartPreset, options: HdChartPrintOptions): Promise<void> {
    if (this.pdfPrint.busy()) return;

    const lang = this.contentLang.effectiveLang();
    // De contenttaal kan van de UI-taal verschillen: zorg dat haar vertalingen geladen zijn.
    await firstValueFrom(this.transloco.load(lang));
    const withCrosses = options.layout !== 'basic';
    if (withCrosses) await firstValueFrom(this.transloco.load(`${CROSSES_SCOPE}/${lang}`));
    const t = (key: string) => this.transloco.translate(`${PROPERTIES_PREFIX}.${key}`, undefined, lang);
    const ui = (key: string) => this.transloco.translate(`${CONTROLS_PREFIX}.${key}`);

    const renderState = this.renderStateFactory.create(preset.configuration);
    // Zelfde brugberekening als het Basis-paneel, zodat de print toont wat de gebruiker kent.
    const basics = deriveBasicProperties(renderState, preset.planetaryActivations, this.settings.advancedBridgeCalculation());
    const colors = resolveHdChartPrintColors(options.colorScheme, {
      colorConfig: this.settings.colorConfig(),
      planetGridColorConfig: this.settings.planetGridColorConfig(),
    });
    const regular = options.layout === 'regular';
    const conditioning = regular && basics.primaryConditioning.kind !== 'none'
      ? buildHdPrimaryConditioningVm(
          basics.primaryConditioning, t, center => colors.colorConfig.centerDefined[center], this.settings.bridgeGateStyle(),
        )
      : null;
    // Regulier en Mandala: de kruisen op een tweede koprij (wens Steven 2026-10-22).
    const crosses: HdChartPrintCrosses | undefined = withCrosses
      ? {
          incarnationCross: formatHdIncarnationCrossValue(basics.incarnationCross, t, lang, this.gateInfo),
          lifeCross: formatHdLifeCrossValue(deriveLifeCross(preset.planetaryActivations)),
        }
      : undefined;
    const mandala = options.layout === 'mandala' ? this.buildMandala(preset, lang) : null;
    // Regulier: Chiron in de statistieken, zoals in de app (enkel met de Chiron-instelling aan).
    const chironTooltip = this.transloco.translate('humanDesign.chart.simpleGrid.chironTooltip', undefined, lang);
    const chiron = (side: HdChironSide) => regular && this.settings.showChiron()
      ? toHdSimpleChironVm(resolveHdChironPosition(preset.metadata, side), chironTooltip)
      : null;

    this.notifier.info(ui('pdfGenerating'));
    try {
      await this.pdfPrint.download({
        component: HdChartPrintSheetComponent,
        inputs: {
          name: options.name,
          basics: buildHdChartPrintBasics(basics, t, crosses),
          configuration: preset.configuration,
          designActivations: preset.planetaryActivations.filter(activation => activation.side === 'design'),
          personalityActivations: preset.planetaryActivations.filter(activation => activation.side === 'personality'),
          colorConfig: colors.colorConfig,
          planetGridColorConfig: colors.planetGridColorConfig,
          layout: options.layout,
          // Keynotes enkel bij Basis (wens Steven 2026-10-22): naast de LPS-grids en in het wiel is er geen plaats.
          showCenterKeynotes: options.layout === 'basic' && options.centerKeynotes,
          renderState: regular ? renderState : null,
          visualizationMode: this.settings.planetGridVisualizationMode(),
          conditioning,
          chironDesign: chiron('design'),
          chironPersonality: chiron('personality'),
          mandala,
        },
        fileName: safeFileName(`${options.name} - chart`, 'pdf'),
        lang,
        title: `${options.name} - Mechanics in Motion`,
      });
    } catch (error) {
      const busy = error instanceof PdfPrintError && error.kind === 'busy';
      this.notifier.error(ui(busy ? 'pdfBusy' : 'pdfFailed'));
      if (!busy) console.error('PDF-print mislukt', error);
    }
  }

  /** Het wiel met alle ringen, met de mandala-instellingen van de app (kwartaalkleuren, dierenriem). */
  private buildMandala(preset: HdBodygraphChartPreset, lang: string): HdChartPrintMandala {
    const layout = buildMandalaWheelLayout({ zodiacRing: true, quarterRing: true, ichingRing: true, godheadRing: true });
    const translate = (key: string) => this.transloco.translate(key, {}, lang);
    return {
      geometry: layout.geometry,
      scale: layout.scale,
      gateGlyphs: buildHdMandalaGateGlyphs(preset.planetaryActivations, 'base', hdMandalaSideGlyphColor),
      quarterTexts: buildHdMandalaQuarterTexts(translate),
      zodiacSignNames: buildHdMandalaZodiacNames(translate),
      quarterColorVariant: this.settings.mandalaQuarterColorVariant(),
      zodiacRingVariant: this.settings.mandalaZodiacRingVariant(),
      sliceCenterFill: this.settings.mandalaSliceCenterFade(),
    };
  }
}
